/**
 * orbital_clouds — the Commons' cloud shell seen from orbit, the PURE half (shader gaps R4, 2026-09-30 — the owner's
 * report §6.6).
 *
 * The planets from orbit were a texture, a terminator, a glint and an atmosphere shell; the ground's clouds (the
 * CloudModule's field and decks) never reached orbit, so a storm you could see from a hill was nowhere from the ship.
 * The Commons now wears a thin transparent SHELL (`CLOUD_SHELL_K` × its radius, inside the 2.5 % atmosphere shell) whose
 * coverage is baked from THE SAME WEATHER FIELD the ground renders (`lib/weather.ts` meteoAt → resolveWeather's
 * `cloudCoverage`), so the storm you see from orbit is the storm you land in:
 *
 *  - THE MAP: the ground square (±`COMMONS_HALF_M`) is laid on the sphere exactly as the NIGHT LIGHTS are
 *    (`planet_look.cityLightsTexture`: u = ½ + x/2e6 · 0.92, v = ½ + z/2e6 · 0.62 — `CITY_MAP`), so a storm cell sits over
 *    the city lights of the towns it rains on. Outside that patch (the far hemisphere, the poles) the sphere carries a
 *    seeded, slowly drifting 3D-noise field on the unit DIRECTION — isotropic, so it has no pole pinch — blended in over
 *    the patch edge.
 *  - THE FIELD, CHEAPLY: meteoAt costs ~375 µs a sample (three terrain evaluations for the orographic lift and the lapse
 *    rate); a 1024×512 bake is ~300 k patch texels. `orbitalWeatherAt` is resolveWeather's coverage path with the
 *    orographic term and the elevation as INPUTS (0 by default) — the rest (the biome climate, the two advected synoptic
 *    noise octaves, the diurnal cycle, precipitation and its 7–8-okta floor, dust, the live-ops / debug pins) line for
 *    line; `verify:space-light` proves it BIT-EXACT against the real resolveWeather wherever the orographic term is zero
 *    (a settlement plateau's level interior). The orographic part is then restored as a coarse RESIDUAL (the real
 *    resolveWeather minus the cheap one on a node grid, bilinear) — a hill's own cloud is sub-texel from orbit anyway.
 *    The noise here (`wxNoise`) is a MIRROR of weather.ts's private `vnoise` + `latticeHash` (the harness pins it).
 *  - THE TEXTURE: RGBA8 — R coverage, G thickness (cloudDensity), B blowing dust (a sandstorm is a tan haze from orbit),
 *    A precipitation; the GPU builds its mips; the shell samples it with the sphere's own uv (continuous across the
 *    seam — SphereGeometry duplicates its seam vertices), so the derivatives choose the mip and the poles blur instead
 *    of pinching (Tarini 2012; Golus, "Distinctive Derivative Differences"). The surface's cloud SHADOW looks the same
 *    texture up at the point where the sun ray leaves the shell, with the surface uv's own gradients (seam-free).
 *  - TIME: fronts advect at 11 m/s — a bake every `BAKE_EVERY_S` is 0.3 of a texel of motion, cross-faded over
 *    `BLEND_S` between two textures (never a pop).
 *  - What NOT to build (Nubis³, Schneider 2023): a voxel cloudscape for a flyable sky is the ground's job; from orbit a
 *    2-D coverage map with detail noise (the EVE / Kerbal recipe: a main map + a detail texture + projected shadows) is
 *    what the eye resolves on a 78 km world.
 *
 * NO-CYCLE RULE: numbers in, numbers out (weather.ts's exports and the shared terrain's biome weights only).
 */
import type { Biome } from '@shared/terrain.js';
import { biomeWeights } from '@shared/terrain.js';
import { BIOME_WEATHER, prevailingWind, FRONT_ADVECT_M_PER_S, SYNOPTIC_WAVELENGTH_M, PRECIP_COVER_FLOOR, PRECIP_COVER_GAIN } from '../weather.js';

export type V3 = { x: number; y: number; z: number };
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
function smooth(e0: number, e1: number, v: number): number { const t = clamp01((v - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }

// ── the map ──────────────────────────────────────────────────────────────────────────────────────────────────────
/** The night-lights mapping (planet_look.cityLightsTexture) — ONE mapping for lights and clouds. */
export const CITY_MAP = { span: 2_000_000, ku: 0.92, kv: 0.62 } as const;
/** Half the Commons square (the 2,048 km shard). */
export const COMMONS_HALF_M = 1_024_000;
/** Ground (x, z) → the equirect's (u, v) (v in the DATA-ROW convention: row = v·H; three's sphere uv().y). */
export function groundToUv(x: number, z: number): { u: number; v: number } {
  return { u: 0.5 + (x / CITY_MAP.span) * CITY_MAP.ku, v: 0.5 + (z / CITY_MAP.span) * CITY_MAP.kv };
}
export function uvToGround(u: number, v: number): { x: number; z: number } {
  return { x: ((u - 0.5) / CITY_MAP.ku) * CITY_MAP.span, z: ((v - 0.5) / CITY_MAP.kv) * CITY_MAP.span };
}
/** three's SphereGeometry: x = −cos φ sin θ, y = cos θ, z = sin φ sin θ with u = φ/2π, v = 1 − θ/π. */
export function uvToDir(u: number, v: number): V3 {
  const phi = u * 2 * Math.PI, theta = (1 - v) * Math.PI;
  return { x: -Math.cos(phi) * Math.sin(theta), y: Math.cos(theta), z: Math.sin(phi) * Math.sin(theta) };
}
export function dirToUv(d: V3): { u: number; v: number } {
  let u = Math.atan2(d.z, -d.x) / (2 * Math.PI);
  if (u < 0) u += 1;
  return { u, v: 1 - Math.acos(Math.max(-1, Math.min(1, d.y))) / Math.PI };
}
/** How much of the ground patch a texel carries (1 inside, 0 outside, a soft edge of `PATCH_EDGE` in ground metres). */
export const PATCH_EDGE_M = 60_000;
export function patchWeight(x: number, z: number): number {
  const e = Math.max(Math.abs(x), Math.abs(z));
  return 1 - smooth(COMMONS_HALF_M - PATCH_EDGE_M, COMMONS_HALF_M, e);
}

// ── the weather field's mirror ───────────────────────────────────────────────────────────────────────────────────
/** MIRROR of lib/weather.ts `latticeHash` (FNV-1a over seed|salt, the lattice, the murmur3 fmix32) — pinned. */
function latticeHash(seed: string, ix: number, iz: number, salt: string): number {
  let h = 2166136261;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= ix | 0; h = Math.imul(h, 16777619);
  h ^= iz | 0; h = Math.imul(h, 16777619);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/** MIRROR of lib/weather.ts `vnoise` (smoothstep-interpolated value noise) — pinned. */
export function wxNoise(seed: string, u: number, v: number, salt: string): number {
  const x0 = Math.floor(u), z0 = Math.floor(v);
  const fx = u - x0, fz = v - z0;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const a = latticeHash(seed, x0, z0, salt), b = latticeHash(seed, x0 + 1, z0, salt);
  const c = latticeHash(seed, x0, z0 + 1, salt), d = latticeHash(seed, x0 + 1, z0 + 1, salt);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}
const SYNOPTIC_WAVELENGTH2_M = 2200; // weather.ts (private)
const LAPSE_C_PER_M = 6.5 / 1000;    // weather.ts (private)
const SNOW_WETBULB_C = 0.9;          // weather.ts (private)
const DUST_WAVELENGTH_M = 26000;     // weather.ts (private)
const OVERRIDE_INTENSITY: Record<string, number> = { clear: 0.0, cloudy: 0.55, fog: 0.5, rain: 0.75, storm: 1.0, snow: 0.8, sandstorm: 0.9 }; // weather.ts (private)

export interface OrbitalWeather { cover: number; thick: number; dust: number; precip: number }
/**
 * resolveWeather's cloud path at (x, z) with the OROGRAPHIC lift and the ELEVATION as inputs (0 = none): the biome
 * climate, the advected synoptic anomalies, the diurnal cycle, precipitation and its okta floor, blowing dust and the
 * live-ops / debug pins — line for line (bit-exact where the orographic term is zero; `verify:space-light`).
 */
export function orbitalWeatherAt(seed: string, x: number, z: number, tSec: number, tod: number, override?: string, elev = 0, orographic = 0): OrbitalWeather {
  const w = biomeWeights(seed, x, z);
  // meteoAt's climate loop (its summation order — bit-exactness)
  let baseT = 0, swing = 0, baseRH = 0, baseWind = 0;
  for (const b of Object.keys(w) as Biome[]) {
    const bw = w[b], p = BIOME_WEATHER[b];
    baseT += p.baseTempC * bw; swing += p.diurnalSwingC * bw; baseRH += p.baseRH * bw; baseWind += p.windStrength * bw;
  }
  const wind = prevailingWind(seed);
  const ax = x - wind.x * FRONT_ADVECT_M_PER_S * tSec;
  const az = z - wind.z * FRONT_ADVECT_M_PER_S * tSec;
  const r1x = ax * 0.891 - az * 0.454, r1z = ax * 0.454 + az * 0.891;
  const r2x = ax * 0.970 + az * 0.242, r2z = -ax * 0.242 + az * 0.970;
  const n1 = wxNoise(seed, r1x / SYNOPTIC_WAVELENGTH_M, r1z / SYNOPTIC_WAVELENGTH_M, 'wh');
  const n2 = wxNoise(seed, r2x / SYNOPTIC_WAVELENGTH2_M, r2z / SYNOPTIC_WAVELENGTH2_M, 'wh2');
  const hAnom = ((n1 * 0.72 + n2 * 0.28) * 2 - 1);
  const tAnom = (wxNoise(seed, (ax + 31337) / 9000, (az - 71993) / 9000, 'wt') * 2 - 1) * 3.5;
  const diurnal = Math.cos((tod - 0.604) * Math.PI * 2);
  const night = clamp01(Math.cos(tod * Math.PI * 2) * 0.5 + 0.5);
  const e = Math.max(0, elev);
  const temperatureC = baseT + tAnom + diurnal * swing * 0.5 - e * LAPSE_C_PER_M;
  const humidity01 = clamp01(baseRH + hAnom * 0.26 + orographic + night * 0.1 - Math.max(0, temperatureC - 30) * 0.008);
  const cover01 = clamp01(smooth(0.35, 0.95, humidity01) + hAnom * 0.06);
  let precip01 = smooth(0.74, 0.96, humidity01) * (cover01 > 0.3 ? 1 : 0);
  const wetBulbC = temperatureC - (1 - humidity01) * 4.2;
  const instability01 = clamp01((temperatureC - 21) / 13) * humidity01;
  if (precip01 > 0.55 && instability01 > 0.5) precip01 = Math.min(1, precip01 * 1.35);
  const kind: 'none' | 'rain' | 'snow' = precip01 <= 0.02 ? 'none' : wetBulbC <= SNOW_WETBULB_C ? 'snow' : 'rain';
  const wind01a = clamp01(baseWind * (0.55 + Math.abs(hAnom) * 0.7) + precip01 * 0.25 + instability01 * 0.15);
  const dustW = clamp01((w.dune_desert ?? 0) + (w.regolith_plains ?? 0) * 0.15);
  const gust = wxNoise(seed, (ax - 40411) / DUST_WAVELENGTH_M, (az + 20177) / DUST_WAVELENGTH_M, 'dust');
  const dust01 = dustW * smooth(0.6, 0.86, gust) * (1 - smooth(0.2, 0.45, humidity01)) * smooth(0.25, 0.7, wind01a + 0.25 * dustW);

  // resolveWeather's coverage loop (its own order)
  let coverage = 0, density = 0, baseRH2 = 0;
  for (const b of Object.keys(w) as Biome[]) {
    const bw = w[b], p = BIOME_WEATHER[b];
    coverage += p.cloudCoverage * bw; density += p.cloudDensity * bw; baseRH2 += p.baseRH * bw;
  }
  let particleKind = kind as 'none' | 'rain' | 'snow' | 'dust';
  let precip = precip01;
  let dust = dust01;
  let coverage2 = clamp01(coverage + (humidity01 - baseRH2) * 1.1 + precip * 0.12);
  if (override && override in OVERRIDE_INTENSITY) {
    const intensity = OVERRIDE_INTENSITY[override];
    const shift = (intensity - 0.5) * 2 * 0.18;
    coverage2 = clamp01(coverage2 + shift);
    if (override === 'snow') { particleKind = 'snow'; precip = 0.8; coverage2 = Math.max(coverage2, 0.5); }
    else if (override === 'rain') { particleKind = 'rain'; precip = 0.75; coverage2 = Math.max(coverage2, 0.5); }
    else if (override === 'storm') { particleKind = particleKind === 'snow' ? 'snow' : 'rain'; precip = 1; coverage2 = Math.max(coverage2, 0.62); }
    else if (override === 'sandstorm') { particleKind = 'none'; precip = 0; dust = Math.max(dust, smooth(0.08, 0.4, dustW) * 0.92); }
    else { particleKind = 'none'; precip = 0; if (override === 'clear') coverage2 = Math.min(coverage2, 0.1); }
  }
  if (coverage2 < 0.25) { particleKind = 'none'; precip = 0; }
  if (particleKind === 'none' || precip <= 0.02) { precip = 0; if (particleKind !== 'dust') particleKind = 'none'; }
  if (dust > 0.05 && precip <= 0.15) { particleKind = 'dust'; precip = 0; } else dust = precip > 0.15 ? 0 : dust;
  if (particleKind !== 'dust') dust = 0;
  const storm = precip > 0.85;
  if (precip > 0) coverage2 = Math.max(coverage2, PRECIP_COVER_FLOOR + PRECIP_COVER_GAIN * precip);
  if (dust > 0) coverage2 = Math.max(coverage2, 0.35 * dust);
  const cloudDensity = density * (storm ? 1.25 : 1) * (1 + 0.8 * precip);
  return { cover: coverage2, thick: clamp01(cloudDensity / 2.2), dust: clamp01(dust), precip: clamp01(precip) };
}

// ── the far field (off the ground patch) ─────────────────────────────────────────────────────────────────────────
const h3 = (ix: number, iy: number, iz: number, s: number): number => {
  let h = (Math.imul(ix, 0x8da6b343) ^ Math.imul(iy, 0xd8163841) ^ Math.imul(iz, 0xcb1ab31f) ^ s) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};
function noise3(x: number, y: number, z: number, s: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy), uz = fz * fz * (3 - 2 * fz);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number): number => h3(x0 + dx, y0 + dy, z0 + dz, s);
  return l(l(l(c(0, 0, 0), c(1, 0, 0), ux), l(c(0, 1, 0), c(1, 1, 0), ux), uy), l(l(c(0, 0, 1), c(1, 0, 1), ux), l(c(0, 1, 1), c(1, 1, 1), ux), uy), uz);
}
export function seedHash(seed: string): number { let h = 2166136261; for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
/** The far field's drift: a full turn about the pole axis every 6 h (weather systems off the charted square). */
export const FAR_DRIFT_RAD_PER_S = (2 * Math.PI) / (6 * 3600);
/** Coverage of the far field at a unit direction (isotropic 3-D noise — no pole pinch). */
export function farWeatherAt(d: V3, tSec: number, sh: number): OrbitalWeather {
  const a = tSec * FAR_DRIFT_RAD_PER_S;
  const ca = Math.cos(a), sa = Math.sin(a);
  const x = d.x * ca - d.z * sa, z = d.x * sa + d.z * ca, y = d.y;
  const f = 0.55 * noise3(x * 3 + 11.3, y * 3 - 4.1, z * 3 + 7.7, sh) + 0.3 * noise3(x * 7, y * 7, z * 7, sh ^ 0x51ed) + 0.15 * noise3(x * 15, y * 15, z * 15, sh ^ 0x2a2a);
  const band = 0.12 * Math.cos(y * Math.PI * 3); // wetter mid-latitudes, drier subtropics (a hint of Hadley cells)
  const cover = clamp01(smooth(0.38, 0.72, f + band));
  return { cover, thick: clamp01(0.35 + cover * 0.4), dust: 0, precip: clamp01(smooth(0.8, 0.95, cover)) };
}

// ── the bake ─────────────────────────────────────────────────────────────────────────────────────────────────────
/** The coverage texture's size (RGBA8, 2 MB) and the residual grid over the ground patch. */
export const CLOUD_TEX_W = 1024, CLOUD_TEX_H = 512;
export const RESIDUAL_NX = 40, RESIDUAL_NZ = 40;
/** A new bake every minute, cross-faded over 20 s. */
export const BAKE_EVERY_S = 60;
export const BLEND_S = 20;
export interface CloudBakeOpts {
  seed: string; tSec: number; tod: number; override?: string;
  W?: number; H?: number;
  /** The REAL coverage at a ground point (resolveWeather.cloudCoverage) for the residual grid — omitted = no residual. */
  real?: (x: number, z: number) => number;
}
const FAR_NONE: OrbitalWeather = { cover: 0, thick: 0, dust: 0, precip: 0 };
export interface CloudBake { W: number; H: number; data: Uint8Array; patchMean: number; farMean: number; residualRms: number; residualNodes: number }
/**
 * Bake the RGBA8 coverage equirect. Rows can be baked in slices (`rows`) for a main-thread fallback; the residual grid
 * (computed once, on the first slice) restores the orographic lift the cheap path leaves out.
 */
export function bakeClouds(o: CloudBakeOpts, out?: CloudBake, rows?: [number, number], residual?: Float32Array | null): { bake: CloudBake; residual: Float32Array | null } {
  const W = o.W ?? CLOUD_TEX_W, H = o.H ?? CLOUD_TEX_H;
  const bake = out ?? { W, H, data: new Uint8Array(W * H * 4), patchMean: 0, farMean: 0, residualRms: 0, residualNodes: 0 };
  const sh = seedHash(o.seed);
  // the residual grid: real − cheap at the nodes (the orographic lift + the lapse, regionally)
  let res = residual ?? null;
  if (!res && o.real) {
    res = new Float32Array(RESIDUAL_NX * RESIDUAL_NZ);
    let sq = 0;
    for (let j = 0; j < RESIDUAL_NZ; j++) for (let i = 0; i < RESIDUAL_NX; i++) {
      const x = -COMMONS_HALF_M + ((i + 0.5) / RESIDUAL_NX) * 2 * COMMONS_HALF_M;
      const z = -COMMONS_HALF_M + ((j + 0.5) / RESIDUAL_NZ) * 2 * COMMONS_HALF_M;
      const r = o.real(x, z) - orbitalWeatherAt(o.seed, x, z, o.tSec, o.tod, o.override).cover;
      res[j * RESIDUAL_NX + i] = r; sq += r * r;
    }
    bake.residualRms = Math.sqrt(sq / res.length); bake.residualNodes = res.length;
  }
  const resAt = (x: number, z: number): number => {
    if (!res) return 0;
    const fx = clamp01((x + COMMONS_HALF_M) / (2 * COMMONS_HALF_M)) * RESIDUAL_NX - 0.5, fz = clamp01((z + COMMONS_HALF_M) / (2 * COMMONS_HALF_M)) * RESIDUAL_NZ - 0.5;
    const i0 = Math.max(0, Math.min(RESIDUAL_NX - 1, Math.floor(fx))), j0 = Math.max(0, Math.min(RESIDUAL_NZ - 1, Math.floor(fz)));
    const i1 = Math.min(RESIDUAL_NX - 1, i0 + 1), j1 = Math.min(RESIDUAL_NZ - 1, j0 + 1);
    const tx = clamp01(fx - i0), tz = clamp01(fz - j0);
    const g = (i: number, j: number): number => res![j * RESIDUAL_NX + i];
    return (g(i0, j0) * (1 - tx) + g(i1, j0) * tx) * (1 - tz) + (g(i0, j1) * (1 - tx) + g(i1, j1) * tx) * tz;
  };
  const [r0, r1] = rows ?? [0, H];
  let pSum = 0, pN = 0, fSum = 0, fN = 0;
  for (let j = r0; j < r1; j++) {
    const v = (j + 0.5) / H;
    for (let i = 0; i < W; i++) {
      const u = (i + 0.5) / W;
      const g = uvToGround(u, v);
      const pw = patchWeight(g.x, g.z);
      const far = pw < 1 ? farWeatherAt(uvToDir(u, v), o.tSec, sh) : FAR_NONE;
      let wx = far;
      if (pw > 0) {
        const near = orbitalWeatherAt(o.seed, g.x, g.z, o.tSec, o.tod, o.override);
        const cover = clamp01(near.cover + resAt(g.x, g.z));
        wx = { cover: cover * pw + far.cover * (1 - pw), thick: near.thick * pw + far.thick * (1 - pw), dust: near.dust * pw, precip: near.precip * pw + far.precip * (1 - pw) };
        if (pw >= 1) { pSum += cover; pN++; }
      } else { fSum += far.cover; fN++; }
      const k = (j * W + i) * 4;
      bake.data[k] = Math.round(clamp01(wx.cover) * 255);
      bake.data[k + 1] = Math.round(clamp01(wx.thick) * 255);
      bake.data[k + 2] = Math.round(clamp01(wx.dust) * 255);
      bake.data[k + 3] = Math.round(clamp01(wx.precip) * 255);
    }
  }
  if (!rows || rows[0] === 0) { bake.patchMean = 0; bake.farMean = 0; }
  bake.patchMean += pN ? pSum / pN * ((r1 - r0) / H) : 0;
  bake.farMean += fN ? fSum / fN * ((r1 - r0) / H) : 0;
  return { bake, residual: res };
}

// ── the shell ────────────────────────────────────────────────────────────────────────────────────────────────────
/** The shell's radius (× the planet's): inside the 2.5 % atmosphere shell, a few hundred metres over the 78 km sea. */
export const CLOUD_SHELL_K = 1.008;
/** How dark a full cloud's shadow is on the surface (the rest is the sky light under an overcast). */
export const CLOUD_SHADOW_K = 0.6;
/** The detail noise that carves cells out of the coverage (the ground cloud renderer's rule: a cloud where the noise
 *  sits under the coverage). `cloudAlphaAt` is the CPU mirror of the shell's alpha. */
export const CLOUD_DETAIL = { freq: 9.0, octaves: 4, lacunarity: 2.1, gain: 0.55, edge: 0.18 } as const; // the bed's pictures: a ±0.09 edge read as cut-out cotton
/** The shell's optical-depth scale: a full cell of mean thickness hides ~75 % of the surface at normal incidence. */
export const CLOUD_KAPPA = { base: 0.9, thick: 1.3 } as const;
export function cloudCellMask(cover: number, noise01: number): number { return smooth(noise01 - CLOUD_DETAIL.edge, noise01 + CLOUD_DETAIL.edge, cover); }
/** The shell's opacity along a view ray: thicker clouds and grazing rays (the limb) hide more, never all of it. */
export function cloudAlphaAt(mask: number, thick: number, muView: number): number {
  const mu = Math.max(0.1, Math.abs(muView));
  return Math.min(0.96, 1 - Math.exp(-(mask * (CLOUD_KAPPA.base + CLOUD_KAPPA.thick * thick)) / mu));
}
/** Where the sun ray from a surface point (planet-centred, |P| = R) leaves the shell of radius Rc. */
export function shellExit(P: V3, L: V3, Rc: number): V3 {
  const b = P.x * L.x + P.y * L.y + P.z * L.z;
  const c = P.x * P.x + P.y * P.y + P.z * P.z - Rc * Rc;
  const t = -b + Math.sqrt(Math.max(0, b * b - c));
  return { x: P.x + L.x * t, y: P.y + L.y * t, z: P.z + L.z * t };
}
/** The cross-fade weight of the newer bake, `age` seconds after it landed. */
export function cloudBlend(age: number): number { return clamp01(age / BLEND_S); }
